(() => {
  const PROPERTY = '_tier_reward';
  let listenersBound = false;
  let syncing = false;
  let syncQueued = false;
  let syncTimer = -1;

  function moneyFormat(cents, format, currencyCode) {
    const value = (Number(cents) || 0) / 100;
    const amount = value.toFixed(2);
    const amountNoDec = Math.round(value).toString();
    const grouped = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const plainFormat = String(format || '${{amount}}').replace(/<[^>]*>/g, '');
    let formatted = plainFormat
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDec.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDec)
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, grouped.replace(/,/g, "'"))
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
    if (currencyCode && formatted.indexOf(currencyCode) === -1) {
      formatted = `${currencyCode} ${formatted}`;
    }
    return formatted;
  }

  function markerPositions(count) {
    if (count <= 1) return [100];
    return Array.from({ length: count }, (_, index) => ((index + 1) / count) * 100);
  }

  function decodeEntities(value) {
    if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  function decodeConfigStrings(value) {
    if (typeof value === 'string') return decodeEntities(value);
    if (Array.isArray(value)) return value.map(decodeConfigStrings);
    if (value && typeof value === 'object') {
      const out = {};
      Object.keys(value).forEach((key) => {
        out[key] = decodeConfigStrings(value[key]);
      });
      return out;
    }
    return value;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatTierLabel(label, tierIndex) {
    const text = String(label || '').trim();
    if (Number(tierIndex) !== 2) return escapeHtml(text);
    const parts = text.split(' ');
    if (parts.length < 2) return escapeHtml(text);
    const last = parts.pop();
    return `${escapeHtml(parts.join(' '))}<br>${escapeHtml(last)}`;
  }

  function fillPercent(subtotal, tiers) {
    const positions = markerPositions(tiers.length);
    if (!tiers.length) return 0;
    if (subtotal >= tiers[tiers.length - 1].minCents) return 100;
    if (subtotal < tiers[0].minCents) {
      return (subtotal / tiers[0].minCents) * positions[0];
    }
    for (let i = 1; i < tiers.length; i += 1) {
      if (subtotal < tiers[i].minCents) {
        const span = tiers[i].minCents - tiers[i - 1].minCents;
        const t = span > 0 ? (subtotal - tiers[i - 1].minCents) / span : 1;
        return positions[i - 1] + t * (positions[i] - positions[i - 1]);
      }
    }
    return 100;
  }

  function audRate(config) {
    const shop = String((config && config.shopCurrency) || 'AUD').toUpperCase();
    const presentment = String((config && config.presentmentCurrency) || shop).toUpperCase();
    if (presentment === shop) return 1;

    const currency = window.Shopify && window.Shopify.currency;
    const rate = currency ? parseFloat(currency.rate) : NaN;
    if (Number.isFinite(rate) && rate > 0) return rate;
    return 1;
  }

  function minCents(tier, config) {
    const dollars = parseFloat(tier.min);
    if (!Number.isFinite(dollars)) return 0;
    const cents = Math.round(dollars * 100);
    if (!tier.convertFromAud) return cents;
    return Math.round(cents * audRate(config));
  }

  function sectionTargets() {
    const cartDrawer = document.querySelector('cart-drawer[data-section-id]');
    const pageHeader = document.querySelector('page-header[data-section-id]');
    const cartForm = document.querySelector('cart-form[data-section-id]');
    const els = [];
    if (cartDrawer) els.push(cartDrawer);
    if (pageHeader) els.push(pageHeader);
    if (cartForm && !cartDrawer) els.push(cartForm);
    return els;
  }

  function applySections(data) {
    if (!data || !data.sections) {
      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      return;
    }
    sectionTargets().forEach((el) => {
      const html = data.sections[el.dataset.sectionId];
      if (!html) return;
      if (typeof el.updateFromCartChange === 'function') {
        el.updateFromCartChange(html);
      } else if (typeof el.refreshFromHtml === 'function') {
        el.refreshFromHtml(html);
      }
    });
  }

  async function postCart(url, body, refresh) {
    const payload = { ...body };
    if (refresh) {
      const els = sectionTargets();
      if (els.length) {
        payload.sections = els.map((el) => el.dataset.sectionId).join(',');
        payload.sections_url = window.location.pathname;
      }
    }
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.description || data.message || response.status);
    }
    if (refresh) applySections(data);
    return data;
  }

  class TierRewards extends HTMLElement {
    connectedCallback() {
      bindListeners();
      this.sync();
    }

    parseConfig() {
      const node = this.querySelector('[data-tier-rewards-config]');
      if (!node) return null;
      try {
        return decodeConfigStrings(JSON.parse(node.textContent));
      } catch (error) {
        console.warn('[Tier rewards] Invalid config', error);
        return null;
      }
    }

    resolvedTiers(config) {
      return (config.tiers || [])
        .map((tier) => {
          const cents = minCents(tier, config);
          return { ...tier, minCents: cents };
        })
        .filter((tier) => tier.minCents > 0)
        .sort((a, b) => a.minCents - b.minCents);
    }

    renderBar(config) {
      const tiers = this.resolvedTiers(config);
      if (!tiers.length) return tiers;

      const subtotal = Number(config.subtotal) || 0;
      const positions = markerPositions(tiers.length);
      const progress = Math.max(0, Math.min(100, fillPercent(subtotal, tiers)));
      const fill = this.querySelector('[data-tier-rewards-fill]');
      if (fill) fill.style.width = `${progress}%`;

      const reachedTier = [...tiers].reverse().find((tier) => subtotal >= tier.minCents);
      const reachedIndex = reachedTier ? Number(reachedTier.index) || 0 : 0;
      this.classList.remove(
        'tier-rewards--tier-0',
        'tier-rewards--tier-1',
        'tier-rewards--tier-2',
        'tier-rewards--tier-3',
        'tier-rewards--pending-fx'
      );
      this.classList.add(`tier-rewards--tier-${reachedIndex}`);

      const markers = this.querySelector('[data-tier-rewards-markers]');
      if (markers) {
        markers.innerHTML = tiers
          .map((tier, index) => {
            const left = positions[index];
            const reached = subtotal >= tier.minCents;
            const posClass = index === 0 ? ' is-first' : index === tiers.length - 1 ? ' is-last' : '';
            const tierIndex = Number(tier.index) || index + 1;
            const labelHtml = formatTierLabel(tier.label || '', tierIndex);
            return `<div class="tier-rewards__marker${reached ? ' is-reached' : ''}${posClass}" data-tier="${tierIndex}" style="left:${left}%">
              <span class="tier-rewards__dot"></span>
              <span class="tier-rewards__label">${labelHtml}</span>
            </div>`;
          })
          .join('');
      }

      const next = tiers.find((tier) => subtotal < tier.minCents);
      const highest = [...tiers].reverse().find((tier) => subtotal >= tier.minCents);
      const messageEl = this.querySelector('[data-tier-rewards-message]');
      if (messageEl) {
        if (next) {
          const remaining = moneyFormat(next.minCents - subtotal, config.moneyFormat, config.presentmentCurrency);
          const template = next.message || `You're {{amount}} away from ${next.label}!`;
          messageEl.textContent = template.replace(/\{\{\s*amount\s*\}\}/gi, remaining);
        } else if (highest) {
          const template = config.unlockedTemplate || "You've unlocked {{ label }}!";
          messageEl.textContent = template.replace(/\{\{\s*label\s*\}\}/gi, highest.label || '');
        } else {
          messageEl.textContent = '';
        }
      }

      return tiers;
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    desiredGifts(config, tiers) {
      const subtotal = Number(config.subtotal) || 0;
      const desired = [];
      tiers.forEach((tier) => {
        if (subtotal < tier.minCents) return;
        if (tier.type !== 'product' && tier.type !== 'product_variant') return;
        const variantId = this.pickVariantId(tier, config.gifts);
        if (variantId) desired.push({ tier: String(tier.index), variantId });
      });
      return desired;
    }

    pickVariantId(tier, gifts) {
      const existing = (gifts || []).find((gift) => String(gift.tier) === String(tier.index));
      if (existing && existing.variantId) return Number(existing.variantId);
      if (tier.defaultVariantId) return Number(tier.defaultVariantId);
      const available = (tier.sizeVariants || []).find((variant) => variant.available && variant.id);
      return available ? Number(available.id) : 0;
    }

    async syncGifts(config, tiers) {
      if (syncing) {
        syncQueued = true;
        return;
      }
      const desired = this.desiredGifts(config, tiers);
      const current = config.gifts || [];
      const toAdd = desired.filter((item) => !current.some((gift) => String(gift.tier) === item.tier));
      const toRemove = current.filter((gift) => !desired.some((item) => item.tier === String(gift.tier)));

      if (!toAdd.length && !toRemove.length) return;

      syncing = true;
      try {
        if (toAdd.length) {
          await postCart(theme.routes.cartAdd, {
            items: toAdd.map((item) => ({
              id: item.variantId,
              quantity: 1,
              properties: { [PROPERTY]: item.tier }
            }))
          }, !toRemove.length);
        }
        if (toRemove.length) {
          const updates = {};
          toRemove.forEach((gift) => {
            if (gift.key) updates[gift.key] = 0;
          });
          if (Object.keys(updates).length) {
            await postCart(theme.routes.cartUpdate, { updates }, true);
          }
        }
      } catch (error) {
        console.warn('[Tier rewards] Gift sync failed', error);
      } finally {
        syncing = false;
        if (syncQueued) {
          syncQueued = false;
          scheduleSync();
        }
      }
    }

    sync() {
      const config = this.parseConfig();
      if (!config) return;
      const tiers = this.renderBar(config);
      this.syncGifts(config, tiers);
    }

    async changeSize(select) {
      const newId = Number(select.value);
      const oldKey = select.dataset.key;
      const tier = select.dataset.tierReward;
      if (!newId || !oldKey || newId === Number(select.dataset.currentVariantId)) return;

      syncing = true;
      select.disabled = true;
      try {
        await postCart(theme.routes.cartAdd, {
          items: [{
            id: newId,
            quantity: 1,
            properties: { [PROPERTY]: String(tier || '') }
          }]
        }, false);
        await postCart(theme.routes.cartUpdate, { updates: { [oldKey]: 0 } }, true);
      } catch (error) {
        console.warn('[Tier rewards] Size change failed', error);
        select.value = select.dataset.currentVariantId || select.value;
      } finally {
        select.disabled = false;
        syncing = false;
        if (syncQueued) {
          syncQueued = false;
          scheduleSync();
        }
      }
    }
  }

  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      document.querySelectorAll('tier-rewards').forEach((el) => {
        if (typeof el.sync === 'function') el.sync();
      });
    }, 50);
  }

  function bindListeners() {
    if (listenersBound) return;
    listenersBound = true;
    document.addEventListener('on:cart:after-merge', scheduleSync);
    window.addEventListener('pageshow', scheduleSync);
    document.addEventListener('change', (event) => {
      const select = event.target.closest('[data-tier-reward-size]');
      if (!select) return;
      const host = document.querySelector('tier-rewards');
      if (host && typeof host.changeSize === 'function') host.changeSize(select);
    });
  }

  bindListeners();

  if (!customElements.get('tier-rewards')) {
    customElements.define('tier-rewards', TierRewards);
  }
})();
